import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { AnimatedWrapper } from '@atlassian/ai-common';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { AIAnswerError } from '../../ai-answer-dialog/ai-answer-body/ai-answer-result/ai-answer-error';
import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { SmartAnswerFooterSection } from '../../ai-answer-dialog/ai-answer-footer/ai-answer-footer';
import { FollowUpSuggestionFooter } from '../../ai-answer-dialog/ai-answer-footer/followup-suggestion-footer';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
	getSearchAIAnalyticsContextData,
	onAIAnswerResultCompleteShown,
	onAIAnswerResultFirstByteShown,
	SearchAIAnalyticsContextLocation,
} from '../../ai-answer-dialog/analytics';
import { SearchAIAnalyticsContext } from '../../ai-answer-dialog/search-ai-analytics-context';
import { type NLPSearchResultSource, type SourceSmartCardType } from '../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { AISourcesCarousel } from '../../common/ui/ai-sources';
import { useKnowledgeSmartCardAnalytics } from '../../common/ui/knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';
import { SourcesRightRail } from '../../common/ui/sources-right-rail/sources-right-rail';
import { SourcesSidePanel } from '../../common/ui/sources-side-panel/sources-side-panel';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import type { QueryIntent } from '../../constants';
import { useBufferedContent } from '../common/hooks/use-buffered-content';
import { useContentTruncationDetector } from '../common/hooks/use-content-truncation-detector';
import { useDelayedResizeObserver } from '../common/hooks/use-delayed-resize-observer';
import { AICollapse } from '../common/ui/ai-collapse';
import { AIDisclaimerAndFeedback } from '../common/ui/ai-disclaimer-and-feedback';
import { AnimatedSingleLineShowMore } from '../common/ui/animated-single-line-show-more';
import { OpenChatInputBox } from '../common/ui/open-chat-input-box';
import { ReactRenderer } from '../common/ui/react-renderer';
import { ReadMoreButton } from '../common/ui/read-more-button';
import { SmartAnswerCardBodyGenerating } from '../common/ui/smart-answer-card-body-generating';

import { messages } from './messages';

// Memoized renderer component outside main component
const MemoizedRenderer = React.memo(function MemoizedRenderer({
	contentToRender,
	showAnimation,
}: {
	contentToRender: string;
	showAnimation: boolean;
}) {
	return (
		<ReactRenderer
			key="stable-renderer"
			markdown={contentToRender}
			animateComponents={showAnimation}
		/>
	);
});

const styles = cssMap({
	// Force the smart answer card to be the full max width of
	// the search page layout body main content container
	fullWidth: {
		width: '100vw',
		maxWidth: '100%',
		marginBottom: token('space.150'),
	},
	interactive: {
		cursor: 'pointer',
	},
	root: {
		minHeight: '198px',
	},
	fullHeight: {
		height: '100%',
	},
	disclaimer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
	followUpChatInputSectionStyles: {
		marginTop: token('space.400'),
	},
	disclaimerWrapperStyles: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
	},
	answerContentFullWidth: {
		flexGrow: 1,
		flexShrink: 1,
		minWidth: '0px',
	},
	sourcesRailTitle: {
		// Keep in sync with sources-right-rail container styles
		width: '314px',
		minWidth: '314px',
		marginLeft: token('space.300'),
	},
	disclaimerFullWidthSpacing: {
		marginTop: token('space.150'),
		paddingTop: token('space.100'),
	},
	sectionContainer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
	sourcesTopPadding: {
		paddingTop: token('space.050'),
	},
	nonInteractive: {
		pointerEvents: 'none',
	},
	loadingPaddingOverrides: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	standardSAINMarginOverrides: {
		marginBottom: token('space.100'),
	},
});

type SmartAnswerCardConsolidatedProps = {
	onFMPReached?: () => void;
	onAnswerExpandedChange?: (expanded: boolean) => void;
	queryIntent?: QueryIntent;
};

function SmartAnswerCardConsolidatedComponent({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
}: SmartAnswerCardConsolidatedProps) {
	const [collapsed, setCollapsed] = useState(true);
	const containerRef = useRef<HTMLDivElement>(null);
	const [didUserTriggerCollapse, setDidUserTriggerCollapse] = useState(false);
	const [isVisibleTextComplete, setIsVisibleTextComplete] = useState(false);
	const [showAnimation, setShowAnimation] = useState(false);

	const componentRenderTimeRef = useRef<number>(Date.now());
	const firstByteEventFiredRef = useRef(false);

	const context = useAIAnswerContext();
	const { commonAttributes, hasError, isAnswered, loading, partialResponse } = context;
	const { queryHash, searchSessionId, answerString, sources, errorStateSuggestedQueries } =
		commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const streamingText = partialResponse?.partialNlpResult ?? '';
	const [streamingTextToDisplay, setStreamingTextToDisplay] = useState<string>(streamingText);

	// Refs for content truncation detection
	const isVisibleTextCompleteRef = useRef(false);
	const completeAnswerAnalyticsEventFiredRef = useRef(false);
	const renderedContentRef = useRef<HTMLDivElement>(null);
	const contentContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// If streaming stopped but buffered content still exists, clear it to reset the display
		if (!streamingText && streamingTextToDisplay && fg('kd_fix_abort_errors')) {
			setStreamingTextToDisplay('');
			setIsVisibleTextComplete(false);
			return;
		}
		if (collapsed && !isVisibleTextComplete) {
			setStreamingTextToDisplay(streamingText);
		} else if (!collapsed) {
			setStreamingTextToDisplay(streamingText);
		}
	}, [collapsed, isVisibleTextComplete, streamingText, streamingTextToDisplay]);

	// Notify context when collapsed state changes
	useEffect(() => {
		onAnswerExpandedChange?.(!collapsed);
	}, [collapsed, onAnswerExpandedChange]);

	useEffect(() => {
		if (!loading && !hasError && !isVisibleTextComplete) {
			setIsVisibleTextComplete(true);
			onFMPReached?.(); // Mark FMP as reached for cached answers
		}
	}, [loading, hasError, isVisibleTextComplete, onFMPReached]);

	// Track if we should show animations (if loading or partialResponse are true)
	useEffect(() => {
		if (!showAnimation && loading && partialResponse) {
			setShowAnimation(true);
		}
		if (loading && !partialResponse && showAnimation && fg('kd_fix_abort_errors')) {
			setShowAnimation(false);
			firstByteEventFiredRef.current = false;
			completeAnswerAnalyticsEventFiredRef.current = false;
		}
	}, [loading, partialResponse, showAnimation]); // use useEffect to trigger analytics event in order to avoid needing to
	// pass commonAttributes as a dependency to handleExpandCollapseButtonClick
	// and thus avoid re-rendering SmartAnswerCardBodyStreaming with every
	// update to commonAttributes (which gets updated with the streamed text)
	useEffect(() => {
		if (didUserTriggerCollapse) {
			fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes));
		}
	}, [didUserTriggerCollapse, commonAttributes, fireAnalyticsEvent]);

	// Detect if content is truncated when collapsed
	const isContentTruncated = useContentTruncationDetector(
		renderedContentRef,
		contentContainerRef,
		collapsed,
		[streamingText],
	);

	// Complete answer analytics
	useEffect(() => {
		// Only fire once and ensure DOM element is ready with meaningful content
		if (completeAnswerAnalyticsEventFiredRef.current || !renderedContentRef.current) {
			return;
		}
		const answerHeight = renderedContentRef.current.getBoundingClientRect().height;
		const containerHeight = contentContainerRef.current?.getBoundingClientRect().height ?? 0;
		const whitespaceHeight = Math.max(containerHeight - answerHeight, 0);

		// Only fire if we have meaningful height (content is rendered)
		if (answerHeight > 0 && containerHeight > 0) {
			fireAnalyticsEvent(
				onAIAnswerResultCompleteShown({
					...commonAttributes,
					answerHeight,
					containerHeight,
					whitespaceHeight,
					queryIntent: queryIntent,
				}),
			);
			completeAnswerAnalyticsEventFiredRef.current = true;
		}
	}, [fireAnalyticsEvent, commonAttributes, queryIntent, isContentTruncated]);

	// SAIN usage event
	useEffect(() => {
		fireSAINUsageEvent({ commonAttributes });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleExpandCollapseButtonClick = useCallback(() => {
		if (collapsed) {
			setCollapsed(false);
			setDidUserTriggerCollapse(false);
		} else {
			setCollapsed(true);
			setDidUserTriggerCollapse(true);
		}
	}, [collapsed]);

	const handleVisibleTextComplete = useCallback(() => {
		setIsVisibleTextComplete(true);
		onFMPReached?.(); // Mark FMP as reached for streaming answers
	}, [onFMPReached]);

	const handleContainerClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			const selection = window.getSelection()?.toString() ?? '';
			// Highlighting text should not expand the card
			const hasHighlightedText = selection.length > 0;
			// Clicking in a feedback modal or flag should not expand the card
			const isClickingOnContainer = containerRef.current?.contains(event.target as Node) ?? false;

			if (collapsed && !hasHighlightedText && isClickingOnContainer) {
				setCollapsed(false);
				fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
				fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
			}
		},
		[collapsed, fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent],
	);

	const { handleSmartCardClick } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
	});

	// UFO resize observer for streaming
	const resizeObserverCallback = useCallback(() => {
		if (
			!isVisibleTextCompleteRef.current &&
			renderedContentRef.current &&
			contentContainerRef.current &&
			!!streamingTextToDisplay
		) {
			const renderedContentRefHeight = renderedContentRef.current.getBoundingClientRect().height;
			const contentContainerRefHeight = contentContainerRef.current.getBoundingClientRect().height;
			const isVisibleTextComplete = renderedContentRefHeight > contentContainerRefHeight;
			if (isVisibleTextComplete) {
				handleVisibleTextComplete();
				isVisibleTextCompleteRef.current = true;
			}
		}
	}, [handleVisibleTextComplete, streamingTextToDisplay]);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	useDelayedResizeObserver(renderedContentRef, resizeObserverCallback, 100, [
		streamingTextToDisplay,
	]);

	const bufferedContent = useBufferedContent(streamingTextToDisplay, isAnswered && !loading);

	useEffect(() => {
		if (bufferedContent.length > 0 && !firstByteEventFiredRef.current) {
			fireAnalyticsEvent(
				onAIAnswerResultFirstByteShown({
					...commonAttributes,
					timeToFirstByte: Date.now() - componentRenderTimeRef.current,
				}),
			);
			firstByteEventFiredRef.current = true;
		}
	}, [bufferedContent, commonAttributes, fireAnalyticsEvent]);

	// Use answerString when complete, bufferedContent when streaming
	const contentToRender = isAnswered ? answerString : bufferedContent;

	// Experiments
	const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
		abTestId: 'kd_sain_show_no_answer_suggestions_exp',
	});

	const showReadMoreButton = collapsed && isContentTruncated;

	// Single return statement with React component for main content
	const isStreaming = !isAnswered && (loading || loading === undefined);

	// Early return for error state
	if (hasError) {
		return (
			<Box xcss={styles.fullWidth}>
				<AIAnswerError />
			</Box>
		);
	}

	// Early return for generating state
	if (!isAnswered && (loading || loading === undefined)) {
		if (!partialResponse || bufferedContent.length < 1) {
			return fg('kd_team_26_sain_borderless_update') ||
				SAINFeatureControl.isSAINUXUpliftEnabled() ? (
				<Box xcss={cx(styles.fullWidth, styles.loadingPaddingOverrides)}>
					<UFOSegment name="search-ai-dialog-all-text">
						<UFOLoadHold name="search-ai-dialog-all-text-loading" hold />
						<UFOSegment name="search-ai-dialog-visible-text">
							<UFOLoadHold name="search-ai-dialog-visible-text-loading" hold />
							<SmartAnswerCardBodyGenerating
								rightRail={
									SAINFeatureControl.isSourcesFullWidthEnabled() ? <SourcesRightRail /> : undefined
								}
							/>
							<AnimatedSingleLineShowMore isStreaming showMoreButtonVisible={false} />
						</UFOSegment>
					</UFOSegment>
				</Box>
			) : (
				<Box xcss={styles.fullWidth}>
					<UFOSegment name="search-ai-dialog-all-text">
						<UFOLoadHold name="search-ai-dialog-all-text-loading" hold />
						<UFOSegment name="search-ai-dialog-visible-text">
							<UFOLoadHold name="search-ai-dialog-visible-text-loading" hold />
							<AnimatedWrapper>
								<SmartAnswerCardBodyGenerating />
							</AnimatedWrapper>
						</UFOSegment>
					</UFOSegment>
				</Box>
			);
		}
	}

	return (
		<Box
			xcss={cx(
				styles.fullWidth,
				(fg('kd_team_26_sain_borderless_update') || SAINFeatureControl.isSAINUXUpliftEnabled()) &&
					styles.loadingPaddingOverrides,
			)}
			testId="smart-answer-card-consolidated"
		>
			<UFOSegment name="search-ai-dialog-all-text">
				<UFOLoadHold name="search-ai-dialog-all-text-loading" hold={loading} />
				<UFOSegment name="search-ai-dialog-visible-text">
					<UFOLoadHold name="search-ai-dialog-visible-text-loading" hold={!isVisibleTextComplete} />
					{fg('platform_ufo_custom_cohort_data_migration') ? (
						<>
							<UFOCustomCohortData dataKey="queryHash" value={queryHash} />
							<UFOCustomCohortData dataKey="searchSessionId" value={searchSessionId} />
							<UFOCustomCohortData dataKey="queryIntent" value={queryIntent} />
						</>
					) : (
						<UFOCustomData
							data={{
								queryHash,
								searchSessionId,
								queryIntent,
							}}
						/>
					)}
					{/* Main content */}
					{(fg('kd_team_26_sain_borderless_update') ||
						SAINFeatureControl.isSAINUXUpliftEnabled()) && (
						<SmartAnswerCardContent
							isStreaming={isStreaming}
							collapsed={collapsed}
							showReadMoreButton={showReadMoreButton}
							handleExpandCollapseButtonClick={handleExpandCollapseButtonClick}
							contentContainerRef={contentContainerRef}
							renderedContentRef={renderedContentRef}
							sources={sources}
							searchSessionId={searchSessionId}
							sourceClickCallback={handleSmartCardClick}
							errorStateSuggestedQueries={errorStateSuggestedQueries}
							serveErrorStateSuggestedQueriesExp={serveErrorStateSuggestedQueriesExp}
							commonAttributes={commonAttributes}
							contentToRender={contentToRender}
							showAnimation={showAnimation}
							handleContainerClick={handleContainerClick}
							containerRef={containerRef}
						/>
					)}
					{!(
						fg('kd_team_26_sain_borderless_update') || SAINFeatureControl.isSAINUXUpliftEnabled()
					) && (
						<AnimatedWrapper isAnswerComplete={isAnswered && !loading}>
							<SmartAnswerCardContent
								isStreaming={isStreaming}
								collapsed={collapsed}
								showReadMoreButton={showReadMoreButton}
								handleExpandCollapseButtonClick={handleExpandCollapseButtonClick}
								contentContainerRef={contentContainerRef}
								renderedContentRef={renderedContentRef}
								sources={sources}
								searchSessionId={searchSessionId}
								sourceClickCallback={handleSmartCardClick}
								errorStateSuggestedQueries={errorStateSuggestedQueries}
								serveErrorStateSuggestedQueriesExp={serveErrorStateSuggestedQueriesExp}
								commonAttributes={commonAttributes}
								contentToRender={contentToRender}
								showAnimation={showAnimation}
								handleContainerClick={handleContainerClick}
								containerRef={containerRef}
							/>
						</AnimatedWrapper>
					)}
				</UFOSegment>
			</UFOSegment>
		</Box>
	);
}

function SmartAnswerCardContent({
	isStreaming,
	collapsed,
	showReadMoreButton,
	handleExpandCollapseButtonClick,
	contentContainerRef,
	renderedContentRef,
	sources,
	searchSessionId,
	sourceClickCallback,
	errorStateSuggestedQueries,
	serveErrorStateSuggestedQueriesExp,
	commonAttributes,
	contentToRender,
	showAnimation,
	handleContainerClick,
	containerRef,
}: {
	isStreaming: boolean;
	collapsed: boolean;
	showReadMoreButton: boolean;
	handleExpandCollapseButtonClick: () => void;
	contentContainerRef: React.RefObject<HTMLDivElement>;
	renderedContentRef: React.RefObject<HTMLDivElement>;
	sources: NLPSearchResultSource[];
	searchSessionId: string;
	sourceClickCallback: ({
		index,
		source,
		hostname,
		sourceCardType,
	}: {
		index: number;
		source: NLPSearchResultSource;
		hostname?: string;
		sourceCardType: SourceSmartCardType;
	}) => void;
	errorStateSuggestedQueries: string[];
	serveErrorStateSuggestedQueriesExp: boolean;
	commonAttributes: any;
	contentToRender: string;
	showAnimation: boolean;
	handleContainerClick: (event: React.MouseEvent<HTMLDivElement>) => void;
	containerRef: React.RefObject<HTMLDivElement>;
}) {
	const [answerContentHeight, setAnswerContentHeight] = useState<number | undefined>(undefined);

	useDelayedResizeObserver(renderedContentRef, (entries) => {
		const entry = entries[0];
		if (entry) {
			setAnswerContentHeight(entry.contentRect.height);
		}
	});

	const sourcesSection = (
		<Stack space="space.100">
			{!isStreaming &&
				sources.length > 0 &&
				SAINFeatureControl.isSourcesInShortSAINAnswersEnabled() && (
					<Box xcss={cx(styles.sourcesTopPadding, collapsed && styles.nonInteractive)}>
						<AISourcesCarousel
							sources={sources}
							searchSessionId={searchSessionId}
							onClickHandler={sourceClickCallback}
						/>
					</Box>
				)}
		</Stack>
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={cx(
				collapsed ? styles.interactive : undefined,
				(fg('kd_team_26_sain_borderless_update') || SAINFeatureControl.isSAINUXUpliftEnabled()) &&
					styles.standardSAINMarginOverrides,
			)}
			onClick={handleContainerClick}
			ref={containerRef}
		>
			<Stack
				alignBlock="stretch"
				grow="fill"
				spread="space-between"
				xcss={isStreaming ? styles.root : styles.fullHeight}
			>
				<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
					<Box>
						{/* Conditional UI based on experiments */}
						<AICollapse
							title={
								<Inline
									spread="space-between"
									alignBlock="center"
									grow="fill"
									xcss={cx(
										(fg('kd_team_26_sain_borderless_update') ||
											SAINFeatureControl.isSAINUXUpliftEnabled()) &&
											styles.standardSAINMarginOverrides,
									)}
								>
									{fg('kd_team_26_sain_borderless_update') ||
									SAINFeatureControl.isSAINUXUpliftEnabled() ? (
										<Inline alignBlock="center" space="space.050">
											<AiGenerativeTextSummaryIcon label="AI Content" size="medium" />
											<Heading as="h1" size="xsmall">
												<FormattedMessage {...messages.smart_answer_card_dynamic_layout_title} />
											</Heading>
										</Inline>
									) : (
										<Heading as="h2" size="xxsmall">
											<Text color="color.text.subtlest" size="small" weight="bold">
												<FormattedMessage {...messages.smart_answer_card_title} />
											</Text>
										</Heading>
									)}
									{SAINFeatureControl.isSourcesSidePanelEnabled() ? <SourcesSidePanel /> : null}
									{SAINFeatureControl.isSourcesFullWidthEnabled() && sources.length > 0 ? (
										<Box xcss={styles.sourcesRailTitle}>
											<Heading as="h3" size="xsmall">
												<Text color="color.text.subtle" weight="bold">
													<FormattedMessage {...messages.sources_rail_title} />
												</Text>
											</Heading>
										</Box>
									) : null}
								</Inline>
							}
							isCollapsed={collapsed}
							onExpandCollapse={handleExpandCollapseButtonClick}
							ref={contentContainerRef}
							showReadMoreButton={showReadMoreButton}
							luminaAnswerType={
								fg('kd_team_26_sain_borderless_update') ||
								SAINFeatureControl.isSAINUXUpliftEnabled()
									? 'STANDARD'
									: undefined
							}
						>
							{SAINFeatureControl.isSourcesFullWidthEnabled() ? (
								<Inline spread="space-between" alignBlock="start">
									<Box
										ref={renderedContentRef}
										id="ai-answer-result-section"
										testId="sain-fmp-target"
										xcss={styles.answerContentFullWidth}
									>
										<SearchAIAnalyticsContext
											data={getSearchAIAnalyticsContextData({
												location: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
												commonAttributes,
											})}
										>
											<SmartCardProvider>
												<MemoizedRenderer
													contentToRender={contentToRender}
													showAnimation={showAnimation}
												/>
											</SmartCardProvider>
										</SearchAIAnalyticsContext>
										{!isStreaming && (
											<Stack space="space.150" xcss={styles.followUpChatInputSectionStyles}>
												<Inline space="space.100" alignBlock="center">
													<FollowUpSuggestionFooter />
												</Inline>
												<OpenChatInputBox />
											</Stack>
										)}
									</Box>
									<SourcesRightRail
										maxHeight={isStreaming || collapsed ? undefined : answerContentHeight}
									/>
								</Inline>
							) : (
								<Box
									ref={renderedContentRef}
									id="ai-answer-result-section"
									testId="sain-fmp-target"
								>
									<SearchAIAnalyticsContext
										data={getSearchAIAnalyticsContextData({
											location: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
											commonAttributes,
										})}
									>
										<SmartCardProvider>
											<MemoizedRenderer
												contentToRender={contentToRender}
												showAnimation={showAnimation}
											/>
										</SmartCardProvider>
									</SearchAIAnalyticsContext>
									{(fg('kd_team_26_sain_borderless_update') ||
										SAINFeatureControl.isSAINUXUpliftEnabled()) &&
										sourcesSection}
								</Box>
							)}
							{SAINFeatureControl.isSourcesFullWidthEnabled() &&
								!showReadMoreButton &&
								!isStreaming && (
									<Box xcss={cx(styles.disclaimerWrapperStyles, styles.disclaimerFullWidthSpacing)}>
										<AIDisclaimerAndFeedback
											showFeedbackButtons={!isStreaming}
											showCopyButton={false}
											renderedContentRef={renderedContentRef}
										/>
									</Box>
								)}
							{!(
								fg('kd_team_26_sain_borderless_update') ||
								SAINFeatureControl.isSAINUXUpliftEnabled()
							) &&
								!SAINFeatureControl.isSourcesFullWidthEnabled() &&
								sourcesSection}
						</AICollapse>

						{showReadMoreButton &&
							!(
								fg('kd_team_26_sain_show_more_button_update') ||
								SAINFeatureControl.isSAINUXUpliftEnabled()
							) && <ReadMoreButton onClick={handleExpandCollapseButtonClick} />}

						{(showReadMoreButton || isStreaming) &&
							(fg('kd_team_26_sain_show_more_button_update') ||
								SAINFeatureControl.isSAINUXUpliftEnabled()) && (
								<AnimatedSingleLineShowMore
									isStreaming={isStreaming}
									showMoreButtonVisible={showReadMoreButton}
									onClickShowMoreButton={handleExpandCollapseButtonClick}
								/>
							)}
					</Box>

					{/* Sources section - only show when complete and not collapsed */}
					{!isStreaming &&
						!collapsed &&
						sources.length > 0 &&
						!SAINFeatureControl.isSourcesInShortSAINAnswersEnabled() &&
						!SAINFeatureControl.isSourcesFullWidthEnabled() && (
							<Box xcss={styles.sectionContainer}>
								<AISourcesCarousel
									sources={sources}
									searchSessionId={searchSessionId}
									onClickHandler={sourceClickCallback}
								/>
							</Box>
						)}

					{/* Disclaimer section */}
					{!showReadMoreButton &&
						!isStreaming &&
						!SAINFeatureControl.isSourcesFullWidthEnabled() &&
						(fg('kd_team_26_sain_chat_and_followups_update') ||
							SAINFeatureControl.isSAINUXUpliftEnabled()) && (
							<Stack space="space.150" xcss={styles.followUpChatInputSectionStyles}>
								<Inline space="space.100" alignBlock="center">
									<FollowUpSuggestionFooter />
								</Inline>
								<OpenChatInputBox />
								<Box xcss={styles.disclaimerWrapperStyles}>
									<AIDisclaimerAndFeedback
										showFeedbackButtons={!isStreaming}
										showCopyButton={false}
										renderedContentRef={renderedContentRef}
									/>
								</Box>
							</Stack>
						)}

					{!(
						fg('kd_team_26_sain_chat_and_followups_update') ||
						SAINFeatureControl.isSAINUXUpliftEnabled()
					) && (
						<Box xcss={styles.disclaimer}>
							<AIDisclaimerAndFeedback
								showFeedbackButtons={!isStreaming}
								showCopyButton={!isStreaming}
								renderedContentRef={renderedContentRef}
							/>
						</Box>
					)}
				</Stack>

				{/* Footer section - only show when complete */}
				{!isStreaming &&
				!(
					fg('kd_team_26_sain_chat_and_followups_update') ||
					SAINFeatureControl.isSAINUXUpliftEnabled()
				) &&
				(!collapsed ||
					(errorStateSuggestedQueries.length > 0 && serveErrorStateSuggestedQueriesExp)) ? (
					<SmartAnswerFooterSection showOpenChat={true} />
				) : null}
			</Stack>
		</Box>
	);
}

export function SmartAnswerCardConsolidated({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
}: SmartAnswerCardConsolidatedProps): React.JSX.Element {
	return (
		<UFOSegment name="smart-answer-card">
			<SmartAnswerCardConsolidatedComponent
				onFMPReached={onFMPReached}
				onAnswerExpandedChange={onAnswerExpandedChange}
				queryIntent={queryIntent}
			/>
		</UFOSegment>
	);
}
